import React, { useState, useMemo } from 'react';

import { FieldError, UseControllerProps, useFormContext } from 'react-hook-form';
import { useQuery } from 'react-query';
import { getProfiles } from '../../api/profile';
import { useAuth } from '../../contexts/Auth';
import { useDebounce } from 'use-debounce';
import { SearchableSelect } from '../Form/SearchableSelect';
import { match } from 'ts-pattern';
import { getQuerySearchFilters } from '../../libs/utils/profile';
import { BadgeStatus } from '../../interfaces/badge';

type SingleTeacherSelectProps = {
  id: string;
  label: string;
  disabled?: boolean;
  error?: FieldError;
  availableTeacher?: number[];
  lessonTemplateId?: number;
} & UseControllerProps;

const SingleTeacherSelect: React.FC<SingleTeacherSelectProps> = ({
  control,
  name,
  label,
  disabled = false,
  error,
  availableTeacher,
  defaultValue,
  lessonTemplateId,
}) => {
  const [{ token }] = useAuth();
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 500);
  const { watch } = useFormContext();
  const selectedTeacher = watch(name);

  const query = useQuery(['teachersLessons', debouncedSearch], () => {
    const filters: any = {
      $and: getQuerySearchFilters(debouncedSearch),
      isTeacher: true,
    };

    if (availableTeacher && availableTeacher?.length > 0 && lessonTemplateId) {
      filters.lessonTemplates = lessonTemplateId;
    }

    return getProfiles({
      token,
      query: {
        filters,
        pagination: {
          pageSize: 500,
        },
        populate: 'badge',
        forceListing: true,
      },
    });
  });

  // Crea una mappa dei profili per accedere rapidamente al badgeStatus
  const profilesMap = useMemo(() => {
    if (query.status !== 'success' || !query.data?.data) return new Map();
    
    const map = new Map();
    query.data.data.forEach((profile) => {
      map.set(profile.id, profile);
    });
    return map;
  }, [query.data, query.status]);

  // Controlla il badgeStatus del docente selezionato
  const selectedTeacherId = typeof selectedTeacher === 'object' ? selectedTeacher?.value : selectedTeacher;
  const selectedProfile = selectedTeacherId ? profilesMap.get(selectedTeacherId) : null;
  const hasInvalidBadge = selectedProfile?.attributes?.badgeStatus === BadgeStatus.INVALID;

  const options = match(query)
    .with(
      { status: 'error' },
      { status: 'idle' },
      { status: 'loading' },
      () => []
    )
    .with({ status: 'success' }, (query) => {
      const options =
        query.data?.data.map((profile) => ({
          label: profile.attributes.name + ' ' + profile.attributes.surname +'('+ profile.attributes.badge.data?.attributes.badgeNumber +')',
          value: profile.id,
        })) || [];

      if (availableTeacher && availableTeacher?.length > 0) {
        return options.filter(
          (o) => availableTeacher.indexOf(o.value) > -1
        );
      }

      return options;
    })
    .exhaustive();

  return (
    <div>
      <SearchableSelect
        label={label}
        name={name}
        defaultValue={defaultValue}
        disabled={disabled}
        control={control}
        error={error}
        onInput={setSearch}
        isLoading={query.isFetching}
        options={options}
      />
      {hasInvalidBadge && (
        <p className='mt-2 text-sm text-yellow-800 bg-yellow-50 p-2 rounded-lg'>
          Il docente che hai selezionato non ha la tessera attiva per l'anno in corso
        </p>
      )}
    </div>
  );
};

export default SingleTeacherSelect;
